import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getTranslations } from '../utils/translations';
import './Blog.css';

// Helper function to get image path for places
const getPlaceImage = (city, placeName) => {
  // Map place names to image file names (handle special cases)
  const placeNameMap = {
    'vienna': {
      'Schönbrunn Palace': 'schoenbrunn'
    }
  };
  
  // Check if there's a mapped name
  const mappedName = placeNameMap[city] && placeNameMap[city][placeName] 
    ? placeNameMap[city][placeName] 
    : placeName;
  
  const imageName = `blog_${city}_${mappedName}.jpg`;
  return `/images/${imageName}`;
};

const blogArticles = {
  vienna: {
    title: "Vienna, Austria",
    image: "https://images.unsplash.com/photo-1516550893923-42d28e5677af?auto=format&fit=crop&w=1200&q=80",
    intro: "Vienna, the imperial capital of Austria, is a city where history meets modernity. Known for its stunning architecture, classical music heritage, and coffee house culture, Vienna offers an unforgettable travel experience.",
    sections: [
      {
        title: "What to Do in Vienna",
        content: "Vienna is a city of culture and elegance. Start your journey at the magnificent Schönbrunn Palace, a UNESCO World Heritage site that was the summer residence of the Habsburgs. Take a stroll through the historic city center, where you'll find St. Stephen's Cathedral, a Gothic masterpiece. Don't miss the Hofburg Palace, the former imperial palace that now houses several museums. For music lovers, attend a performance at the Vienna State Opera or visit the homes of famous composers like Mozart and Beethoven. Experience the famous Viennese coffee house culture at Café Central or Café Sacher, where you can enjoy a slice of the original Sachertorte."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Schönbrunn Palace",
            description: "The former summer residence of the Habsburgs, featuring 1,441 rooms and beautiful gardens. Take a guided tour to learn about imperial life.",
            image: getPlaceImage("vienna", "schoenbrunn")
          },
          {
            name: "St. Stephen's Cathedral",
            description: "A stunning Gothic cathedral in the heart of Vienna. Climb the South Tower for panoramic views of the city.",
            image: getPlaceImage("vienna", "St. Stephen's Cathedral")
          },
          {
            name: "Hofburg Palace",
            description: "The former imperial palace complex, now home to museums, the Spanish Riding School, and the Austrian National Library.",
            image: getPlaceImage("vienna", "Hofburg Palace")
          },
          {
            name: "Belvedere Palace",
            description: "A beautiful baroque palace complex featuring an impressive art collection, including works by Gustav Klimt.",
            image: getPlaceImage("vienna", "Belvedere Palace")
          },
          {
            name: "Vienna State Opera",
            description: "One of the world's most famous opera houses. Take a guided tour or attend a performance for an unforgettable experience.",
            image: getPlaceImage("vienna", "Vienna State Opera")
          },
          {
            name: "Prater Park",
            description: "A large public park featuring the iconic Wiener Riesenrad (Giant Ferris Wheel) and numerous attractions for all ages.",
            image: getPlaceImage("vienna", "Prater Park")
          }
        ]
      }
    ]
  },
  krakow: {
    title: "Krakow, Poland",
    image: "/images/krakow.jpg",
    intro: "Krakow, Poland's former royal capital, is a city steeped in history and culture. With its well-preserved medieval core, vibrant Jewish quarter, and proximity to Auschwitz, Krakow offers a profound and moving travel experience.",
    sections: [
      {
        title: "What to Do in Krakow",
        content: "Explore the historic Old Town, a UNESCO World Heritage site, and visit the magnificent Wawel Castle, the former residence of Polish kings. Walk through the Main Market Square, one of Europe's largest medieval squares, and admire St. Mary's Basilica with its famous hourly trumpet call. Discover the Jewish Quarter (Kazimierz), now a vibrant cultural district with synagogues, museums, and trendy cafes. Take a day trip to the Wieliczka Salt Mine, an underground wonder with chapels carved from salt. Visit Auschwitz-Birkenau Memorial and Museum for a sobering but important historical experience. Enjoy traditional Polish cuisine at local restaurants and experience the city's lively nightlife."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Wawel Castle",
            description: "The former royal residence, featuring beautiful architecture, royal chambers, and the famous Wawel Dragon. Don't miss the Crown Treasury and Armory.",
            image: getPlaceImage("krakow", "Wawel Castle")
          },
          {
            name: "Main Market Square (Rynek Główny)",
            description: "One of Europe's largest medieval squares, surrounded by historic buildings, cafes, and the Cloth Hall. Perfect for people-watching and enjoying the atmosphere.",
            image: getPlaceImage("krakow", "Main Market Square (Rynek Główny)")
          },
          {
            name: "St. Mary's Basilica",
            description: "A stunning Gothic church famous for its wooden altarpiece and the hourly trumpet call (hejnał) played from the taller tower.",
            image: getPlaceImage("krakow", "St. Mary's Basilica")
          },
          {
            name: "Kazimierz (Jewish Quarter)",
            description: "A historic district with synagogues, Jewish cemeteries, and museums. Now a vibrant area with trendy cafes, restaurants, and cultural venues.",
            image: getPlaceImage("krakow", "Kazimierz (Jewish Quarter)")
          },
          {
            name: "Wieliczka Salt Mine",
            description: "An underground wonder featuring chapels, sculptures, and chambers carved entirely from salt. A UNESCO World Heritage site located just outside Krakow.",
            image: getPlaceImage("krakow", "Wieliczka Salt Mine")
          },
          {
            name: "Auschwitz-Birkenau Memorial",
            description: "A powerful and moving memorial to the victims of the Holocaust. An important historical site that requires respectful visitation.",
            image: getPlaceImage("krakow", "Auschwitz-Birkenau Memorial")
          }
        ]
      }
    ]
  },
  warsaw: {
    title: "Warsaw, Poland",
    image: "/images/warsaw.jpg",
    intro: "Warsaw, Poland's capital, is a city of resilience and rebirth. Completely rebuilt after World War II, Warsaw combines historic charm with modern energy, offering visitors a unique blend of history, culture, and contemporary life.",
    sections: [
      {
        title: "What to Do in Warsaw",
        content: "Explore the meticulously reconstructed Old Town, a UNESCO World Heritage site that was rebuilt from ruins after WWII. Visit the Royal Castle, the former residence of Polish monarchs. Walk along the Royal Route to see palaces, churches, and monuments. Discover the POLIN Museum of the History of Polish Jews, an award-winning museum telling the 1000-year history of Polish Jews. Visit the Warsaw Uprising Museum to learn about the city's heroic resistance. Enjoy the beautiful Łazienki Park, home to the Palace on the Water. Experience Warsaw's vibrant food scene with traditional pierogi and modern Polish cuisine. Take in the city's skyline from the Palace of Culture and Science."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Old Town (Stare Miasto)",
            description: "A beautifully reconstructed medieval quarter, completely rebuilt after WWII. Stroll through cobblestone streets and admire colorful facades.",
            image: getPlaceImage("warsaw", "Old Town (Stare Miasto)")
          },
          {
            name: "Royal Castle",
            description: "The former residence of Polish monarchs, featuring royal apartments, art collections, and beautiful gardens. A symbol of Polish resilience.",
            image: getPlaceImage("warsaw", "Royal Castle")
          },
          {
            name: "POLIN Museum",
            description: "An award-winning museum documenting 1000 years of Jewish history in Poland. Features interactive exhibits and stunning architecture.",
            image: getPlaceImage("warsaw", "POLIN Museum")
          },
          {
            name: "Warsaw Uprising Museum",
            description: "A powerful museum dedicated to the 1944 Warsaw Uprising, featuring multimedia exhibits and personal stories of resistance fighters.",
            image: getPlaceImage("warsaw", "Warsaw Uprising Museum")
          },
          {
            name: "Łazienki Park",
            description: "A beautiful park featuring the Palace on the Water, peacocks, and numerous monuments. Perfect for a peaceful stroll.",
            image: getPlaceImage("warsaw", "Łazienki Park")
          },
          {
            name: "Palace of Culture and Science",
            description: "A controversial but iconic Stalinist skyscraper offering panoramic views of Warsaw from its observation deck.",
            image: getPlaceImage("warsaw", "Palace of Culture and Science")
          }
        ]
      }
    ]
  },
  budapest: {
    title: "Budapest, Hungary",
    image: "/images/budapest.jpg",
    intro: "Budapest, the 'Pearl of the Danube,' is a city of thermal baths, stunning architecture, and rich history. Split by the Danube River into Buda and Pest, this Hungarian capital offers a perfect blend of relaxation and exploration.",
    sections: [
      {
        title: "What to Do in Budapest",
        content: "Relax in one of Budapest's famous thermal baths, such as Széchenyi or Gellért Baths. Explore the historic Buda Castle District, a UNESCO World Heritage site. Walk across the iconic Chain Bridge connecting Buda and Pest. Visit the Hungarian Parliament Building, one of Europe's most beautiful legislative buildings. Take a Danube River cruise to see the city from the water. Climb Gellért Hill for panoramic views. Discover the Great Market Hall for local food and souvenirs. Experience the ruin bars in the Jewish Quarter, unique nightlife venues in abandoned buildings. Visit St. Stephen's Basilica and enjoy the city's vibrant café culture."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Buda Castle",
            description: "A historic castle complex on Castle Hill, offering museums, galleries, and stunning views of the Danube and Pest side.",
            image: getPlaceImage("budapest", "Buda Castle")
          },
          {
            name: "Hungarian Parliament Building",
            description: "An architectural masterpiece on the Danube's banks. Take a guided tour to see the stunning interior and the Hungarian Crown Jewels.",
            image: getPlaceImage("budapest", "Hungarian Parliament Building")
          },
          {
            name: "Széchenyi Thermal Bath",
            description: "One of Europe's largest thermal bath complexes. Relax in the warm thermal waters, both indoors and outdoors.",
            image: getPlaceImage("budapest", "Széchenyi Thermal Bath")
          },
          {
            name: "Chain Bridge",
            description: "The iconic suspension bridge connecting Buda and Pest. Walk across at sunset for stunning views of the city.",
            image: getPlaceImage("budapest", "Chain Bridge")
          },
          {
            name: "Fisherman's Bastion",
            description: "A fairytale-like terrace on Castle Hill offering panoramic views of the Danube and Pest. Perfect for photography.",
            image: getPlaceImage("budapest", "Fisherman's Bastion")
          },
          {
            name: "Great Market Hall",
            description: "A vibrant market featuring local produce, traditional foods, and souvenirs. Try Hungarian specialties like langos and goulash.",
            image: getPlaceImage("budapest", "Great Market Hall")
          }
        ]
      }
    ]
  },
  hallstatt: {
    title: "Hallstatt, Austria",
    image: "/images/hallstatt.jpg",
    intro: "Hallstatt, a picturesque village nestled between a lake and mountains, is one of Austria's most beautiful destinations. This UNESCO World Heritage site offers stunning Alpine scenery, charming architecture, and a rich salt-mining history.",
    sections: [
      {
        title: "What to Do in Hallstatt",
        content: "Take a boat ride on Lake Hallstatt for breathtaking views of the village and surrounding mountains. Explore the historic salt mine, one of the world's oldest, with a fascinating history dating back 7,000 years. Walk through the charming village with its traditional Alpine houses and narrow streets. Visit the Hallstatt Museum to learn about the area's history and archaeology. Climb to the Skywalk for panoramic views of the lake and mountains. Take a cable car to the top of the mountain for hiking and stunning vistas. Visit the Bone House (Beinhaus) with its painted skulls, a unique local tradition. Enjoy local Austrian cuisine at lakeside restaurants."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Lake Hallstatt",
            description: "A stunning alpine lake surrounded by mountains. Take a boat tour or simply enjoy the peaceful waters and scenic views.",
            image: getPlaceImage("hallstatt", "Lake Hallstatt")
          },
          {
            name: "Hallstatt Salt Mine",
            description: "One of the world's oldest salt mines, with a history spanning 7,000 years. Take a guided tour through underground tunnels and slides.",
            image: getPlaceImage("hallstatt", "Hallstatt Salt Mine")
          },
          {
            name: "Skywalk Hallstatt",
            description: "A viewing platform offering breathtaking panoramic views of the lake, village, and surrounding mountains. Accessible by funicular.",
            image: getPlaceImage("hallstatt", "Skywalk Hallstatt")
          },
          {
            name: "Historic Village Center",
            description: "Stroll through the charming village with its traditional Alpine architecture, colorful houses, and narrow cobblestone streets.",
            image: getPlaceImage("hallstatt", "Historic Village Center")
          },
          {
            name: "Bone House (Beinhaus)",
            description: "A unique ossuary containing painted skulls, a local tradition dating back centuries. A fascinating glimpse into local customs.",
            image: getPlaceImage("hallstatt", "Bone House (Beinhaus)")
          },
          {
            name: "Hallstatt Museum",
            description: "Learn about the area's rich history, from prehistoric times to the present, including the famous Hallstatt culture.",
            image: getPlaceImage("hallstatt", "Hallstatt Museum")
          }
        ]
      }
    ]
  },
  paris: {
    title: "Paris, France",
    image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=1200&q=80",
    intro: "Paris, the City of Light, needs no introduction. From the Eiffel Tower to the Louvre, from charming cafes to world-class cuisine, Paris offers an unparalleled travel experience filled with art, culture, and romance.",
    sections: [
      {
        title: "What to Do in Paris",
        content: "Visit the iconic Eiffel Tower and enjoy panoramic views of the city. Explore the Louvre Museum, home to the Mona Lisa and countless masterpieces. Stroll along the Champs-Élysées and visit the Arc de Triomphe. Discover the charming neighborhood of Montmartre and see the Sacré-Cœur Basilica. Take a Seine River cruise to see the city's landmarks from the water. Visit Notre-Dame Cathedral (currently being restored) and admire its Gothic architecture. Explore the Latin Quarter with its historic streets and bookshops. Enjoy world-class French cuisine at local bistros and restaurants. Visit the Palace of Versailles for a day trip. Experience Parisian café culture and indulge in pastries at local patisseries."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Eiffel Tower",
            description: "The iconic symbol of Paris. Take the elevator or climb the stairs for stunning views of the city. Visit at night to see it sparkle.",
            image: getPlaceImage("paris", "Eiffel Tower")
          },
          {
            name: "Louvre Museum",
            description: "The world's largest art museum, home to the Mona Lisa, Venus de Milo, and thousands of other masterpieces. Plan for a full day visit.",
            image: getPlaceImage("paris", "Louvre Museum")
          },
          {
            name: "Notre-Dame Cathedral",
            description: "A masterpiece of French Gothic architecture. While currently being restored, the exterior and surrounding area are still worth visiting.",
            image: getPlaceImage("paris", "Notre-Dame Cathedral")
          },
          {
            name: "Montmartre & Sacré-Cœur",
            description: "A charming hilltop neighborhood with the beautiful Sacré-Cœur Basilica. Enjoy street artists, cafes, and panoramic city views.",
            image: getPlaceImage("paris", "Montmartre & Sacré-Cœur")
          },
          {
            name: "Champs-Élysées & Arc de Triomphe",
            description: "Paris's most famous avenue leading to the Arc de Triomphe. Climb to the top for spectacular views of the city's grand boulevards.",
            image: getPlaceImage("paris", "Champs-Élysées & Arc de Triomphe")
          },
          {
            name: "Palace of Versailles",
            description: "A magnificent royal château just outside Paris. Explore the opulent palace, Hall of Mirrors, and extensive gardens.",
            image: getPlaceImage("paris", "Palace of Versailles")
          }
        ]
      }
    ]
  },
  rome: {
    title: "Rome, Italy",
    image: "https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?auto=format&fit=crop&w=1200&q=80",
    intro: "Rome, the Eternal City, is a living museum where ancient history meets modern life. From the Colosseum to the Vatican, from gelato to pasta, Rome offers an unforgettable journey through time and culture.",
    sections: [
      {
        title: "What to Do in Rome",
        content: "Visit the iconic Colosseum, the largest amphitheater ever built, and imagine gladiatorial combats. Explore the Roman Forum, the heart of ancient Rome. Throw a coin in the Trevi Fountain and make a wish. Visit the Vatican City, including St. Peter's Basilica and the Sistine Chapel. Climb the Spanish Steps and enjoy the view. Discover the Pantheon, one of the best-preserved ancient Roman buildings. Explore Trastevere, a charming neighborhood with narrow streets and authentic restaurants. Enjoy authentic Italian cuisine, from pizza to pasta to gelato. Visit the Borghese Gallery for Renaissance art. Take a stroll through the beautiful Villa Borghese gardens."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Colosseum",
            description: "The iconic symbol of Rome. Take a guided tour to explore the underground chambers and learn about gladiator history.",
            image: getPlaceImage("rome", "Colosseum")
          },
          {
            name: "Vatican City",
            description: "The smallest country in the world, home to St. Peter's Basilica, the Sistine Chapel, and the Vatican Museums with incredible art collections.",
            image: getPlaceImage("rome", "Vatican City")
          },
          {
            name: "Trevi Fountain",
            description: "A stunning Baroque fountain. Throw a coin over your shoulder to ensure your return to Rome. Visit early morning to avoid crowds.",
            image: getPlaceImage("rome", "Trevi Fountain")
          },
          {
            name: "Roman Forum",
            description: "The heart of ancient Rome, featuring ruins of temples, basilicas, and government buildings. Walk in the footsteps of emperors.",
            image: getPlaceImage("rome", "Roman Forum")
          },
          {
            name: "Pantheon",
            description: "One of the best-preserved ancient Roman buildings, featuring a remarkable dome and the tomb of Raphael.",
            image: getPlaceImage("rome", "Pantheon")
          },
          {
            name: "Spanish Steps",
            description: "A famous staircase leading to Trinità dei Monti church. A popular gathering spot and perfect for people-watching.",
            image: getPlaceImage("rome", "Spanish Steps")
          }
        ]
      }
    ]
  },
  zakopane: {
    title: "Zakopane, Poland",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    intro: "Zakopane, Poland's premier mountain resort, is nestled at the foot of the Tatra Mountains. Known for its unique wooden architecture, stunning alpine scenery, and winter sports, Zakopane is a year-round destination for nature lovers.",
    sections: [
      {
        title: "What to Do in Zakopane",
        content: "Take a cable car to Kasprowy Wierch for breathtaking mountain views. Explore the Tatra National Park with its hiking trails and alpine lakes. Visit the charming wooden architecture in the town center, including the famous Villa Koliba. Try traditional highlander cuisine, including oscypek (smoked cheese) and kwaśnica (sour soup). Take a horse-drawn sleigh ride in winter or hiking in summer. Visit the Tatra Museum to learn about local culture and history. Climb Giewont, the iconic mountain with a cross at the summit. Enjoy thermal baths at Chochołowskie Termy. Experience the vibrant Krupówki Street with shops, restaurants, and local crafts. Visit Morskie Oko, one of Poland's most beautiful mountain lakes."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Tatra National Park",
            description: "A stunning national park with hiking trails, alpine meadows, and crystal-clear mountain lakes. Perfect for nature enthusiasts.",
            image: getPlaceImage("zakopane", "Tatra National Park")
          },
          {
            name: "Kasprowy Wierch",
            description: "A mountain peak accessible by cable car, offering panoramic views of the Tatra Mountains. Great for hiking in summer, skiing in winter.",
            image: getPlaceImage("zakopane", "Kasprowy Wierch")
          },
          {
            name: "Morskie Oko",
            description: "One of Poland's most beautiful mountain lakes, accessible by a scenic hike. The emerald-green waters are absolutely stunning.",
            image: getPlaceImage("zakopane", "Morskie Oko")
          },
          {
            name: "Krupówki Street",
            description: "Zakopane's main pedestrian street, lined with shops, restaurants, and cafes. Perfect for shopping and people-watching.",
            image: getPlaceImage("zakopane", "Krupówki Street")
          },
          {
            name: "Giewont",
            description: "An iconic mountain with a large cross at the summit. A challenging but rewarding hike with spectacular views.",
            image: getPlaceImage("zakopane", "Giewont")
          },
          {
            name: "Tatra Museum",
            description: "Learn about the history, culture, and nature of the Tatra region. Features exhibits on highlander traditions and local wildlife.",
            image: getPlaceImage("zakopane", "Tatra Museum")
          }
        ]
      }
    ]
  },
  dubai: {
    title: "Dubai, UAE",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1200&q=80",
    intro: "Dubai, a city of superlatives, is where modern luxury meets traditional culture. From the world's tallest building to man-made islands, from desert safaris to world-class shopping, Dubai offers an extraordinary travel experience.",
    sections: [
      {
        title: "What to Do in Dubai",
        content: "Visit the Burj Khalifa, the world's tallest building, and enjoy panoramic views from the observation deck. Experience the Dubai Mall, one of the world's largest shopping centers, with its aquarium and ice rink. Take a desert safari for dune bashing, camel rides, and traditional entertainment. Visit the Burj Al Arab, the iconic sail-shaped hotel. Explore the historic Al Fahidi neighborhood and Dubai Museum. Take an abra (traditional boat) ride across Dubai Creek. Visit the Palm Jumeirah, a man-made island shaped like a palm tree. Experience the Dubai Fountain show, the world's largest choreographed fountain system. Enjoy world-class dining with views of the city skyline. Visit the Gold Souk and Spice Souk for traditional shopping."
      },
      {
        title: "Top Places to Visit",
        content: [
          {
            name: "Burj Khalifa",
            description: "The world's tallest building at 828 meters. Visit the observation deck on the 124th or 148th floor for breathtaking city views.",
            image: getPlaceImage("dubai", "Burj Khalifa")
          },
          {
            name: "Dubai Mall",
            description: "One of the world's largest shopping malls, featuring over 1,200 stores, an aquarium, ice rink, and the Dubai Fountain.",
            image: getPlaceImage("dubai", "Dubai Mall")
          },
          {
            name: "Palm Jumeirah",
            description: "A man-made island shaped like a palm tree, home to luxury hotels, resorts, and the Atlantis hotel. Take a monorail to explore.",
            image: getPlaceImage("dubai", "Palm Jumeirah")
          },
          {
            name: "Burj Al Arab",
            description: "The iconic sail-shaped hotel, one of the world's most luxurious. Even if not staying, visit for afternoon tea or a meal.",
            image: getPlaceImage("dubai", "Burj Al Arab")
          },
          {
            name: "Dubai Desert Safari",
            description: "Experience dune bashing, camel rides, sandboarding, and traditional Bedouin entertainment in the desert. A must-do activity.",
            image: getPlaceImage("dubai", "Dubai Desert Safari")
          },
          {
            name: "Dubai Creek & Al Fahidi",
            description: "The historic heart of Dubai. Take an abra ride, visit the Dubai Museum, and explore traditional architecture in Al Fahidi.",
            image: getPlaceImage("dubai", "Dubai Creek & Al Fahidi")
          }
        ]
      }
    ]
  }
};

export default function Blog() {
  const { city } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = getTranslations(language);

  // Build article from translations
  const getArticle = (cityKey) => {
    if (!t.blog.cities[cityKey]) return null;
    const cityData = t.blog.cities[cityKey];
    // Get image from blogArticles (images are the same regardless of language)
    const imageData = blogArticles[cityKey];
    // Get English translations to ensure we iterate over English place names (keys)
    const englishTranslations = getTranslations('en');
    const englishCityData = englishTranslations.blog.cities[cityKey];
    const englishPlaceNames = englishCityData ? Object.keys(englishCityData.places) : Object.keys(cityData.places);
    
    return {
      title: cityData.title,
      image: imageData?.image || '',
      intro: cityData.intro,
      sections: [
        {
          title: `${t.blog.whatToDo} ${cityData.title.split(',')[0]}`,
          content: cityData.whatToDoContent
        },
        {
          title: t.blog.topPlaces,
          content: englishPlaceNames.map((englishPlaceName) => {
            // Place names are kept in English as keys in both translation files
            // Descriptions are translated
            return {
              name: englishPlaceName, // Display place name (kept in English as proper nouns)
              description: cityData.places[englishPlaceName] || '',
              image: getPlaceImage(cityKey, englishPlaceName) // Use English name for image path
            };
          })
        }
      ]
    };
  };

  // If no city specified, show blog listing
  if (!city) {
    return (
      <div className="blog-container">
        <button className="blog-back-button" onClick={() => navigate('/')} style={{ marginBottom: '2rem' }}>
          {t.blog.returnToHome}
        </button>
        <div className="blog-header">
          <h1>{t.blog.title}</h1>
          <p>{t.blog.subtitle}</p>
        </div>
        <div className="blog-grid">
          {Object.keys(t.blog.cities).map((key) => {
            const article = getArticle(key);
            if (!article) return null;
            return (
              <div key={key} className="blog-card" onClick={() => navigate(`/blog/${key}`)}>
                <div className="blog-card-image">
                  <img src={blogArticles[key]?.image || article.image} alt={article.title} />
                </div>
                <div className="blog-card-content">
                  <h3>{article.title}</h3>
                  <p>{article.intro.substring(0, 150)}...</p>
                  <button className="blog-read-more">{t.blog.readMore}</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Show specific city article
  const article = getArticle(city);
  if (!article) {
    return (
      <div className="blog-container">
        <div className="blog-error">
          <h2>{t.blog.notFound}</h2>
          <button onClick={() => navigate('/blog')}>{t.blog.backToBlog}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        <button className="blog-back-button" onClick={() => navigate('/blog')}>
          {t.blog.backToBlog}
        </button>
        <button className="blog-back-button" onClick={() => navigate('/')}>
          {t.blog.returnToHome}
        </button>
      </div>
      <article className="blog-article">
        <div className="blog-hero">
          <img src={article.image} alt={article.title} />
          <div className="blog-hero-overlay">
            <h1>{article.title}</h1>
          </div>
        </div>
        <div className="blog-content">
          <p className="blog-intro">{article.intro}</p>
          {article.sections.map((section, index) => (
            <section key={index} className="blog-section">
              <h2>{section.title}</h2>
              {typeof section.content === 'string' ? (
                <>
                  <p>{section.content}</p>
                </>
              ) : (
                <div className="places-grid">
                  {section.content.map((place, placeIndex) => (
                    <div key={placeIndex} className="place-card">
                      <div className="place-image">
                        <img src={place.image} alt={place.name} />
                      </div>
                      <div className="place-content">
                        <h3>{place.name}</h3>
                        <p>{place.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </article>
    </div>
  );
}
